import { IRoughOptions } from '@src/slide/domain/ui-model/element-modeles/property/border/sketch/path-processor/rough.interface';
import {
    NormalizedSegment,
    LineTo,
    CurveTo,
    QuadraticCurveTo,
    CurveSegment,
    ClosePath,
} from '@tencent/alloy-renderer/dist/lib/path';
import { segmentType } from '@src/slide/domain/ui-model/element-modeles/property/border/sketch/path-processor/constants';

export function getRoughenPath(segments: NormalizedSegment[], options: IRoughOptions): string {
    let path = '';
    segments.forEach((segment) => {
        if (segment.cmd === segmentType.MoveTo) {
            path += `M${Math.round(segment.x)} ${Math.round(segment.y)} `;
        } else if (segment.cmd === segmentType.LineTo) {
            path += generateLineToPath(segment, options);
        } else if (segment.cmd === segmentType.CurveTo) {
            path += generateCurveToPath(segment, options);
        } else if (segment.cmd === segmentType.QuadraticCurveTo) {
            path += generateQuadraticCurveToPath(segment, options);
        } else if (segment.cmd === segmentType.ClosePath) {
            const { startX, startY, x, y } = segment;
            if (!(Math.round(startX) === Math.round(x) && Math.round(startY) === Math.round(y))) {
                path += generateLineToPath(segment, options);
            }
            path += 'Z';
        }
    });
    return path;
}

function generateLineToPath(segment: LineTo | ClosePath, options: IRoughOptions): string {
    const { roughness, bowing, roughnessOffset, direction, sizeFactor, seed = Math.random() } = options;

    const { startX, startY, x, y } = segment;
    const midDisX = bowing * (y - startY) / 200;
    const midDisY = bowing * (startX - x) / 200;
    const random = getRoughDistance(roughnessOffset, roughness, seed) * sizeFactor
        * Math.sqrt(getScale(segment, sizeFactor));
    const divergePoint = 0.25 + (Math.random() * 0.1);
    const k = (x !== startX) ? (y - startY) / (x - startX) : 0;
    const scaleX = k > Math.tan(30 * Math.PI / 180) && k < Math.tan(60 * Math.PI / 180) ? 0 : 1;
    const scaleY = k > Math.tan(30 * Math.PI / 180) && k < Math.tan(60 * Math.PI / 180) ? 1.5 : 1;
    const x1 = (midDisX * direction) + startX + ((x - startX) * divergePoint) + (random * direction * scaleX);
    const y1 = (midDisX * direction) + startY + ((y - startY) * divergePoint) + (random * direction * scaleY);
    const x2 = (midDisY * -direction) + startX + (2 * (x - startX) * divergePoint) + (random * -direction * scaleX);
    const y2 = (midDisY * -direction) + startY + (2 * (y - startY) * divergePoint) + (random * -direction * scaleY);

    return `C${Math.round(x1)} ${Math.round(y1)}, ${Math.round(x2)} ${Math.round(y2)}, ${Math.round(segment.x)} ${Math.round(segment.y)} `;
}

function generateQuadraticCurveToPath(segment: QuadraticCurveTo, options: IRoughOptions): string {
    const { roughness, roughnessOffset, sizeFactor, seed = Math.random() } = options;

    const random = getRoughDistance(roughnessOffset, roughness, seed) * sizeFactor * getScale(segment, sizeFactor);

    const x1 = ((segment.startX + (2 * segment.x1)) / 3) + random;
    const y1 = ((segment.startY + (2 * segment.y1)) / 3) + random;
    const x2 = ((segment.x + (2 * segment.x1)) / 3) + random;
    const y2 = ((segment.y + (2 * segment.y1)) / 3) + random;

    // 导出不支持二次贝塞尔曲线，需要将二次贝塞尔曲线转换为三次贝塞尔曲线
    return `C${Math.round(x1)} ${Math.round(y1)}, ${Math.round(x2)} ${Math.round(y2)}, ${Math.round(segment.x)} ${Math.round(segment.y)} `;
}

function generateCurveToPath(segment: CurveTo, options: IRoughOptions): string {
    const { roughness, roughnessOffset, direction, sizeFactor, seed = Math.random() } = options;

    const random = getRoughDistance(roughnessOffset, roughness, seed) * sizeFactor * getScale(segment, sizeFactor);
    const x1 = segment.x1 + (random * direction);
    const y1 = segment.y1 + (random * direction);
    const x2 = segment.x2 + (random * -direction);
    const y2 = segment.y2 + (random * -direction);

    return `C${Math.round(x1)} ${Math.round(y1)}, ${Math.round(x2)} ${Math.round(y2)}, ${Math.round(segment.x)} ${Math.round(segment.y)} `;
}

function getScale(segment: CurveSegment, sizeFactor: number): number {
    const distance = Math.sqrt(Math.pow((segment.startX - segment.x), 2) + Math.pow((segment.startY - segment.y), 2));

    return distance / 200 / sizeFactor;
}

function getRoughDistance(range: number, roughness: number, seed: number): number {
    return Math.abs(roughness * ((seed * range * 2) - range));
}
